import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';
import { AppInfoTooltip } from '../../components/AppInfoTooltip';

interface UnlockedApp {
  appId: string;
  displayName: string;
  // Phase 2.x-c — Application Registry v2 metadata, mode-resolved on the
  // backend. Optional so the client degrades gracefully if PR-A hasn't
  // shipped yet or the entry lacks copy.
  subtitle?: string;
  description?: string;
}

interface Outcome {
  code: string;
  displayName: string;
  description: string;
  icon: string;
  apps: string[];
  /** Apps this outcome unlocks, with display names — for the "unlocks" copy. */
  unlockedApps?: UnlockedApp[];
  category: 'personal' | 'business' | 'productivity';
  pricing: {
    includedCredits: number;
    trialDays: number;
  };
}

interface OnboardingState {
  step: string;
  version: number;
  onboardingMode: 'business' | 'personal' | null;
  workspaceType: 'personal' | 'team' | 'enterprise' | null;
  workspacePurpose: 'personal' | 'business' | 'client' | null;
  selectedOutcomes: string[];
  recommendedOutcomes: string[];
  isComplete: boolean;
}

type OutcomeMode = 'business' | 'personal';

/** Mode-native copy — no abstract "select outcomes" language. */
const MODE_COPY: Record<OutcomeMode, { heading: string; sub: string }> = {
  business: {
    heading: 'Choose what you want your workspace to help you improve.',
    sub: 'Pick the outcomes that matter — we assemble the apps and workflows for each.',
  },
  personal: {
    heading: 'Choose what you want your space to help you organize or strengthen.',
    sub: 'Pick what matters to you — we set up the apps and tools for each.',
  },
};

// SVG icons for outcomes (mapped from Lucide icon names)
const OUTCOME_ICONS: Record<string, JSX.Element> = {
  home: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  users: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  'message-circle': (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  'trending-up': (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  briefcase: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  calendar: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  'graduation-cap': (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
    </svg>
  ),
  shield: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
};

const CATEGORY_LABELS: Record<string, string> = {
  personal: 'Personal',
  business: 'Business',
  productivity: 'Productivity',
};

/**
 * OutcomeSelection page — the onboarding outcomes step.
 *
 * Outcomes are mode-scoped (R2): a business workspace sees business +
 * productivity outcomes; a personal workspace sees personal + productivity
 * outcomes. The list is fetched with `?mode=` so personal-life outcomes
 * never appear in the business path, and vice versa. Each card explains the
 * apps the outcome unlocks. If no outcomes resolve, a recoverable error is
 * shown — never a silent empty screen.
 */
export function OutcomeSelectionPage() {
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState<string[]>([]);
  const [state, setState] = useState<OnboardingState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  // Load onboarding state, then the mode-scoped outcome list.
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const stateRes = await fetch('/api/onboarding/state', { credentials: 'include' });
      const stateData = await stateRes.json();

      let mode: OutcomeMode = 'business';
      if (stateRes.ok && stateData.state) {
        setState(stateData.state);
        mode = stateData.state.onboardingMode === 'personal' ? 'personal' : 'business';
        if (stateData.state.selectedOutcomes?.length > 0) {
          setSelectedOutcomes(stateData.state.selectedOutcomes);
        } else if (stateData.state.recommendedOutcomes?.length > 0) {
          setSelectedOutcomes(stateData.state.recommendedOutcomes);
        }
      }

      // Mode-scoped outcomes — business/personal never see each other's.
      const outcomesRes = await fetch(`/api/focuses?mode=${mode}`, { credentials: 'include' });
      const outcomesData = await outcomesRes.json();

      const resolved: Outcome[] =
        outcomesRes.ok && Array.isArray(outcomesData.outcomes) ? outcomesData.outcomes : [];
      if (resolved.length === 0) {
        // Never a silent empty screen — surface a recoverable error.
        setLoadError("We couldn't load your options. Please try again.");
        setOutcomes([]);
        return;
      }
      setOutcomes(resolved);
    } catch {
      setLoadError("We couldn't load your options. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const mode: OutcomeMode = state?.onboardingMode === 'personal' ? 'personal' : 'business';

  // Group outcomes by category
  const outcomesByCategory = useMemo(() => {
    const grouped: Record<string, Outcome[]> = {};
    for (const outcome of outcomes) {
      if (!grouped[outcome.category]) {
        grouped[outcome.category] = [];
      }
      grouped[outcome.category].push(outcome);
    }
    return grouped;
  }, [outcomes]);

  // Apps preview from the canonical visibility helper. Refetches on every
  // outcome toggle (onboarding is single-user low-volume). Phase 2.x-c
  // PR-B: each app carries the Application Registry v2 subtitle +
  // mode-resolved description straight from /api/onboarding/app-preview
  // — no client-side join, no auxiliary metadata map.
  const [selectedApps, setSelectedApps] = useState<
    Array<{
      appId: string;
      displayName: string;
      subtitle?: string;
      description?: string;
      order: number;
    }>
  >([]);

  useEffect(() => {
    const params = new URLSearchParams({ outcomes: selectedOutcomes.join(',') });
    fetch(`/api/onboarding/app-preview?${params.toString()}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (data?.ok && Array.isArray(data.apps)) {
          setSelectedApps(data.apps);
        } else {
          setSelectedApps([]);
        }
      })
      .catch(() => {
        // Non-blocking — preview stays at the prior state.
      });
  }, [selectedOutcomes]);

  const toggleOutcome = (code: string) => {
    setSelectedOutcomes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleBack = async () => {
    try {
      await fetch('/api/onboarding/back', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors on back navigation
    }

    // The step before outcomes is the mode's setup screen (R7: the route
    // is mode-specific — /onboarding/{mode}/setup).
    navigate(onboardingPath(mode, 'setup', continueUrl));
  };

  const handleContinue = async () => {
    if (selectedOutcomes.length === 0) return;

    setIsSaving(true);
    setError('');

    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ step: 'outcomes', data: { outcomes: selectedOutcomes } }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to save outcomes');
        return;
      }

      navigate(onboardingPath(mode, 'pricing', continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <OnboardingLayout currentStep="outcomes" workspaceType={state?.workspaceType} onboardingMode={mode} wide>
        <div className="bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading your options...</p>
        </div>
      </OnboardingLayout>
    );
  }

  // Recoverable error — never a silent empty outcomes screen.
  if (loadError) {
    return (
      <OnboardingLayout currentStep="outcomes" workspaceType={state?.workspaceType} onboardingMode={mode} wide>
        <div className="bg-white shadow-lg rounded-lg p-8 text-center max-w-md mx-auto">
          <h1 className="text-lg font-semibold text-gray-900">Something went wrong</h1>
          <p className="text-sm text-gray-500 mt-2">{loadError}</p>
          <button
            type="button"
            onClick={() => loadData()}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors"
          >
            Try again
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout currentStep="outcomes" workspaceType={state?.workspaceType} onboardingMode={mode} wide>
      <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">
            {MODE_COPY[mode].heading}
          </h1>
          <p className="text-gray-500 mt-2">{MODE_COPY[mode].sub}</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-6 lg:gap-8">
          {/* LEFT: Outcome cards grouped by category */}
          <div className="lg:col-span-3">
            {Object.entries(outcomesByCategory).map(([category, categoryOutcomes]) => (
              <div key={category} className="mb-8 last:mb-0">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                  {CATEGORY_LABELS[category] || category}
                </h2>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                  {categoryOutcomes.map((outcome) => {
                    const isSelected = selectedOutcomes.includes(outcome.code);
                    const unlocked = outcome.unlockedApps ?? [];
                    return (
                      <button
                        key={outcome.code}
                        type="button"
                        onClick={() => toggleOutcome(outcome.code)}
                        className={`p-5 rounded-xl border-2 text-left transition-all relative ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="w-5 h-5 text-blue-600 absolute top-3 right-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                        <div className="flex items-start gap-4">
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isSelected
                                ? 'bg-blue-100 text-blue-600'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {OUTCOME_ICONS[outcome.icon] || OUTCOME_ICONS.briefcase}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 text-sm sm:text-base">
                              {outcome.displayName}
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                              {outcome.description}
                            </p>
                            {unlocked.length > 0 && (
                              <div className="text-xs text-gray-700 mt-2">
                                <span className="font-medium">Unlocks:</span>{' '}
                                {/*
                                  Phase 2.x-c — each unlocked app shows its
                                  subtitle inline where available, and its
                                  description via tooltip on hover / focus
                                  / tap. Comma-separated inline layout is
                                  preserved so this list stays compact.
                                  Falls back to displayName only when the
                                  registry entry lacks copy.
                                */}
                                {unlocked.map((a, i) => (
                                  <span key={a.appId}>
                                    <AppInfoTooltip
                                      description={a.description}
                                      className={
                                        a.description
                                          ? 'inline-flex items-baseline gap-1 rounded px-0.5 -mx-0.5 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400'
                                          : undefined
                                      }
                                      testId={`unlock-${outcome.code}-${a.appId}`}
                                    >
                                      <span className="font-medium">
                                        {a.displayName}
                                      </span>
                                      {a.subtitle && (
                                        <span className="text-gray-500">
                                          {' '}
                                          — {a.subtitle}
                                        </span>
                                      )}
                                    </AppInfoTooltip>
                                    {i < unlocked.length - 1 && (
                                      <span className="text-gray-500">
                                        {', '}
                                      </span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/*
                              Phase 2.x-c PR-C3a — per-outcome $ badges
                              retired. Outcomes contribute $0 today, so
                              "Included" or "$0/mo" per-outcome implied
                              outcome pricing that doesn't exist. Plan
                              price + capacity live on PricingSummary.
                            */}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT: Preview panel */}
          <div className="lg:col-span-1 order-first lg:order-last">
            <div className="sticky top-4 bg-gray-50 rounded-xl p-5 lg:p-6">
              {selectedOutcomes.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <svg
                    className="w-12 h-12 mx-auto mb-3 opacity-50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <p className="text-sm">
                    {mode === 'business'
                      ? 'Pick an outcome to see the apps it assembles.'
                      : 'Pick something to see the tools it sets up.'}
                  </p>
                </div>
              ) : (
                <>
                  {/* Selected outcomes */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                      Selected ({selectedOutcomes.length})
                    </h3>
                    <ul className="space-y-2">
                      {selectedOutcomes.map((code) => {
                        const outcome = outcomes.find((o) => o.code === code);
                        return (
                          <li key={code} className="flex items-center gap-2 text-sm text-gray-600">
                            <svg
                              className="w-4 h-4 text-green-500 flex-shrink-0"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {outcome?.displayName || code}
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {/* Apps included — Phase 2.x-c PR-B: each chip renders
                      subtitle beneath displayName and exposes description
                      via tooltip. Metadata comes straight from
                      /api/onboarding/app-preview (backend resolves the
                      workspace mode). Registry is the single source of
                      truth; no client-side join. */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                      Apps we'll activate
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedApps.map((app) => (
                        <AppInfoTooltip
                          key={app.appId}
                          description={app.description}
                          className={`px-2.5 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-600 text-left leading-tight ${
                            app.description
                              ? 'hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
                              : ''
                          }`}
                          testId={`preview-app-${app.appId}`}
                        >
                          <span className="block font-medium text-gray-800">
                            {app.displayName}
                          </span>
                          {app.subtitle && (
                            <span className="block text-[10px] text-gray-500">
                              {app.subtitle}
                            </span>
                          )}
                        </AppInfoTooltip>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="px-6 py-2.5 text-gray-600 hover:text-gray-900 transition-colors font-medium"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={selectedOutcomes.length === 0 || isSaving}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
